import { useMemo, useState } from "react";
import { RollbackOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Modal, Tooltip } from "antd";
import { journalsQueries } from "@/entities/journal";
import { CommunicationIcon } from "@/shared/assets/svg/icons";
import { Can } from "@shared/lib/casl";
import { useCoordinator } from "@shared/lib/hooks";
import { UserBubble } from "@shared/ui";
import { useReturnTitleJournal } from "../../../../../journals-side-table/model/hooks/mutate-hooks/use-return-title-journal";
import "./title-rejected-banner.scss";

type TitleStatus = string | undefined;

const ROLE_LABELS = {
  signer: "Подписант",
  approver: "Проверяющий ТЛ",
  sender: "Отправитель в ГСН",
} as const;

function getFioShort(user?: Record<string, unknown> | null): string {
  if (!user) return "";
  const surname = (user.userSurname ?? user.surname ?? "").toString().trim();
  const first = (user.userFirstName ?? user.firstName ?? "").toString().trim();
  const middle = (user.userMiddleName ?? user.middleName ?? "").toString().trim();
  const f = first ? `${first[0]}.` : "";
  const m = middle ? `${middle[0]}.` : "";
  return `${surname} ${f}${m}`.trim();
}

function getPositionFromUserHistory(userHistory: any): string {
  const firstCompany = userHistory?.roleInfo?.userCompaniesHistory?.[0];
  return (firstCompany?.position ?? "").toString().trim();
}

export const TitleRejectedBanner = ({ status }: { status: TitleStatus }) => {
  const { titleId } = useCoordinator();
  const [isReasonOpen, setIsReasonOpen] = useState(false);

  const { data: responsibleData } = useQuery({
    ...journalsQueries.getResponsibleCommentList(titleId || ""),
    enabled: status === "REJECTED" && !!titleId,
  });
  const { data: signersData } = useQuery({
    ...journalsQueries.getJournalSignersList(titleId || ""),
    enabled: status === "REJECTED" && !!titleId,
  });

  const revokerComment = useMemo(() => {
    const candidates: any[] = [];
    const push = (list?: any[]) => {
      (list || []).forEach((item) => {
        if (item?.comment != null && item.comment.toString().trim() !== "") candidates.push(item);
      });
    };
    push((signersData as any)?.signers);
    push((responsibleData as any)?.inputters);
    push((responsibleData as any)?.approvers);
    push((responsibleData as any)?.senders);
    push((responsibleData as any)?.signers);

    const raw = candidates[0]?.comment;
    const text = (raw ?? "").toString().trim();
    return text;
  }, [responsibleData, signersData]);

  const revokerDisplay = useMemo(() => {
    const signers = (signersData as any)?.signers || [];
    const signerWithComment = signers.find((s: any) => s?.comment != null);
    if (signerWithComment) {
      const ui = signerWithComment.userInfo || {};
      const userId = (ui.userId ?? ui.id ?? "").toString().trim();
      return {
        roleLabel: ROLE_LABELS.signer,
        fioShort: getFioShort(ui) || ROLE_LABELS.signer,
        userId: userId || undefined,
        position: (signerWithComment.representativeRole ?? "").toString().trim(),
      };
    }

    const approver = (responsibleData as any)?.approvers?.find(
      (x: any) => x?.comment != null,
    );
    if (approver?.userHistory) {
      const uh = approver.userHistory;
      const userId = (uh.userId ?? "").toString().trim();
      return {
        roleLabel: ROLE_LABELS.approver,
        fioShort: getFioShort(uh) || ROLE_LABELS.approver,
        userId: userId || undefined,
        position: getPositionFromUserHistory(uh),
      };
    }

    const sender = (responsibleData as any)?.senders?.find(
      (x: any) => x?.comment != null,
    );
    if (sender?.userHistory) {
      const uh = sender.userHistory;
      const userId = (uh.userId ?? "").toString().trim();
      return {
        roleLabel: ROLE_LABELS.sender,
        fioShort: getFioShort(uh) || ROLE_LABELS.sender,
        userId: userId || undefined,
        position: getPositionFromUserHistory(uh),
      };
    }

    return null;
  }, [responsibleData, signersData]);

  const { isPending, mutate } = useReturnTitleJournal({ titleId });

  if (status !== "REJECTED") return null;

  const isReasonDisabled = !revokerComment;

  return (
    <>
      <div className="title-rejected-banner">
        <div className="title-rejected-banner__left">
          <div className="title-rejected-banner__icon" aria-hidden="true" />
          <div className="title-rejected-banner__text">
            Титульный лист отклонен, верните в работу для исправления
          </div>
        </div>

        <div className="title-rejected-banner__actions">
          <Tooltip
            title={isReasonDisabled ? "Комментарий не был указан" : revokerComment}
          >
            <Button
              onClick={() => setIsReasonOpen(true)}
              disabled={isReasonDisabled}
              style={{ backgroundColor: "white", color: "rgba(51, 51, 51, 1)" }}
              icon={<CommunicationIcon style={{ marginRight: 5 }} />}
            >
              Узнать причину
            </Button>
          </Tooltip>
          <Can I="edit" a="title_journals">
            <Button type="primary" loading={isPending} onClick={() => mutate()} icon={<RollbackOutlined style={{ marginRight: 5 }} />}>
              Вернуть в работу
            </Button>
          </Can>
        </div>
      </div>

      <Modal
        open={isReasonOpen}
        onCancel={() => setIsReasonOpen(false)}
        footer={null}
        closable={false}
        width={400}
        centered
        className="title-rejected-modal"
      >
        <div className="title-rejected-modal__header">
          <div className="title-rejected-modal__header-left">
            <div className="title-rejected-banner__icon" aria-hidden="true" />
            <div className="title-rejected-modal__title">
              Титульный лист отклонен
            </div>
          </div>
        </div>

        <Can I="edit" a="title_journals">
          <div className="title-rejected-modal__hint">
            Для внесения правок{" "}
            <button
              type="button"
              className="title-rejected-modal__link"
              onClick={() => mutate({ onSuccess: () => setIsReasonOpen(false) })}
              disabled={isPending}
            >
              верните в работу
            </button>
          </div>
        </Can>

        <div className="title-rejected-modal__label">
          Комментарий по правкам
        </div>
        <div className="title-rejected-modal__comment">{revokerComment}</div>

        {revokerDisplay && (
          <div className="title-rejected-modal__person">
            <div className="title-rejected-modal__person-title">
              {revokerDisplay.roleLabel}{" "}
              {revokerDisplay.userId ? (
                <UserBubble userId={revokerDisplay.userId} trigger={["hover", "click"]}>
                  <span className="title-rejected-modal__person-name">
                    {revokerDisplay.fioShort}
                  </span>
                </UserBubble>
              ) : (
                <span className="title-rejected-modal__person-name">
                  {revokerDisplay.fioShort}
                </span>
              )}
            </div>
            {revokerDisplay.position ? (
              <div className="title-rejected-modal__person-desc">
                {revokerDisplay.position}
              </div>
            ) : null}
          </div>
        )}

        <div className="title-rejected-modal__footer">
          <Can I="edit" a="title_journals">
            <Button
              type="primary"
              block
              loading={isPending}
              onClick={() => mutate({ onSuccess: () => setIsReasonOpen(false) })}
            >
              Вернуть в работу
            </Button>
          </Can>
        </div>
      </Modal>
    </>
  );
};

